import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import store from "./redux/store";
import PublicLayout from "./components/common/PublicLayout";
import DashboardLayout from "./components/common/DashboardLayout";
import ProtectedRoute from "./components/common/ProtectedRoute";
import AuthInitializer from "./components/common/AuthInitializer";
import { lazy, Suspense } from "react";
import ScrollToTop from "./components/assets/ScrollToTop";
import PageLoader from "./components/assets/PageLoader";
import UserInitiatedLocation from "./pages/static/LocationChecker";

const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./components/auth/Login"));
const Register = lazy(() => import("./components/auth/Register"));

const MachineSearch = lazy(() => import("./pages/MachineSearch"));
const MachineDetails = lazy(() => import("./pages/MachineDetails"));
const VendorRegister = lazy(() => import("./pages/VendorRegister"));

const About = lazy(() => import("./pages/static/About"));
const Contact = lazy(() => import("./pages/static/Contact"));
const Help = lazy(() => import("./pages/static/Help"));
const Terms = lazy(() => import("./pages/static/Terms"));
const Privacy = lazy(() => import("./pages/static/Privacy"));

const BookingCheckout = lazy(() => import("./pages/customer/BookingCheckout"));

const MyBookings = lazy(() => import("./pages/customer/MyBookings"));

const ViewBooking = lazy(() => import("./pages/customer/ViewBooking"));

const Profile = lazy(() => import("./pages/customer/Profile"));

const VendorDashboard = lazy(
  () => import("./components/vendor/VendorDashboard"),
);

const AddMachine = lazy(() => import("./pages/vendor/AddMachine"));

const VendorOrders = lazy(() => import("./pages/vendor/VendorOrders"));

const VendorMachines = lazy(() => import("./pages/vendor/VendorMachines"));

const EditMachine = lazy(() => import("./pages/vendor/EditMachine"));

const AdminHome = lazy(() => import("./pages/admin/AdminHome"));

const AdminUsers = lazy(() => import("./pages/admin/Users"));

const AdminVendors = lazy(() => import("./pages/admin/Vendors"));

const AdminMachines = lazy(() => import("./pages/admin/Machines"));

const AdminBookings = lazy(() => import("./pages/admin/Bookings"));

const AdminCategories = lazy(() => import("./pages/admin/Categories"));

const AddSubCategory = lazy(() => import("./pages/admin/AddSubCategory"));

const NotFound = lazy(() => import("./pages/404"));

function App() {
  return (
    <Provider store={store}>
      <Router>
        <ScrollToTop />
        <AuthInitializer>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ===== PUBLIC ===== */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/vendor/register" element={<VendorRegister />} />
                <Route path="/machines" element={<MachineSearch />} />
                <Route path="/machines/:id" element={<MachineDetails />} />
                {/* Static Pages */}
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/help" element={<Help />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/location" element={<UserInitiatedLocation />} />
              </Route>
              
              <Route element={<ProtectedRoute allowedRoles={["customer"]} />}>
                <Route element={<DashboardLayout />}>
                  <Route
                    path="/booking/checkout"
                    element={<BookingCheckout />}
                  />
                  <Route path="/bookings" element={<MyBookings />} />
                  <Route path="/bookings/:id" element={<ViewBooking />} />
                </Route>
              </Route>

              {/* ===== SHARED ===== */}
              <Route
                element={
                  <ProtectedRoute
                    allowedRoles={["customer", "vendor", "admin"]}
                  />
                }
              >
                <Route element={<DashboardLayout />}>
                  <Route path="/profile" element={<Profile />} />
                </Route>
              </Route>

              {/* ===== VENDOR ===== */}
              <Route element={<ProtectedRoute allowedRoles={["vendor"]} />}>
                <Route element={<DashboardLayout />}>
                  <Route
                    path="/vendor/dashboard"
                    element={<VendorDashboard />}
                  />
                  <Route path="/vendor/machines/add" element={<AddMachine />} />
                  <Route path="/vendor/orders" element={<VendorOrders />} />
                  <Route path="/vendor/machines" element={<VendorMachines />} />
                  <Route
                    path="/vendor/machines/edit/:id"
                    element={<EditMachine />}
                  />
                </Route>
              </Route>

              {/* ===== ADMIN ===== */}
              <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
                <Route element={<DashboardLayout />}>
                  <Route path="/admin/dashboard" element={<AdminHome />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/vendors" element={<AdminVendors />} />
                  <Route path="/admin/machines" element={<AdminMachines />} />
                  <Route path="/admin/bookings" element={<AdminBookings />} />
                  <Route
                    path="/admin/categories"
                    element={<AdminCategories />}
                  />
                  <Route
                    path="/admin/subcategories/add"
                    element={<AddSubCategory />}
                  />
                </Route>
              </Route>

              {/* ===== 404 ===== */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ToastContainer position="top-right" autoClose={3000} theme="dark" />
        </AuthInitializer>
      </Router>
    </Provider>
  );
}

export default App;
